/**
 * Shared form fields for create / edit listing.
 */
import React from 'react';
import { FaPlus } from 'react-icons/fa';
import { YInput } from '../../../ui/YInput';
import { YSelect } from '../../../ui/YSelect';
import { YFormField } from '../../../ui/YFormField';
import { PlaceSearchInput } from '../../../components/ui/PlaceSearchInput';
import { LISTING_CATEGORIES, LISTING_CONDITIONS, MAX_PHOTOS } from '../constants/communityListings';

const LABEL_CLASS = 'y-label y-label--dialog';

export function CommunityListingFormFields({
  form,
  onFieldChange,
  onLocationTextChange,
  onLocationSelect,
  showPhotos = false,
  photoFiles = [],
  fileInputRef,
  onPhotoSelect,
  onClearPhotos,
}) {
  return (
    <div className="community-listing-form">
      <YFormField label="Title" htmlFor="listing-title" required labelClassName={LABEL_CLASS}>
        <YInput
          id="listing-title"
          value={form.title}
          onChange={onFieldChange('title')}
          maxLength={200}
          placeholder="What are you selling?"
          required
        />
      </YFormField>

      <YFormField label="Category" htmlFor="listing-category" required labelClassName={LABEL_CLASS}>
        <YSelect
          id="listing-category"
          value={form.category}
          onChange={onFieldChange('category')}
          required
        >
          <option value="">Select category</option>
          {LISTING_CATEGORIES.map((c) => (
            <option key={c.value} value={c.value}>
              {c.label}
            </option>
          ))}
        </YSelect>
      </YFormField>

      <div className="community-listing-form__row community-listing-form__row--2">
        <YFormField label="Price" htmlFor="listing-price" labelClassName={LABEL_CLASS}>
          <YInput
            id="listing-price"
            type="number"
            min={0}
            step={0.01}
            value={form.price}
            onChange={onFieldChange('price')}
            placeholder="0.00"
          />
        </YFormField>
        <YFormField label="Condition" htmlFor="listing-condition" labelClassName={LABEL_CLASS}>
          <YSelect
            id="listing-condition"
            value={form.condition}
            onChange={onFieldChange('condition')}
          >
            <option value="">Select condition</option>
            {LISTING_CONDITIONS.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </YSelect>
        </YFormField>
      </div>

      <YFormField label="Description" htmlFor="listing-description" labelClassName={LABEL_CLASS}>
        <YInput
          id="listing-description"
          type="textarea"
          rows={4}
          value={form.description}
          onChange={onFieldChange('description')}
          placeholder="Describe your item, include specs, condition details…"
        />
      </YFormField>

      {showPhotos ? (
        <YFormField
          label={`Photos (optional, max ${MAX_PHOTOS})`}
          htmlFor="listing-photos"
          labelClassName={LABEL_CLASS}
          help={
            photoFiles.length > 0
              ? `${photoFiles.length} photo${photoFiles.length !== 1 ? 's' : ''} selected — uploads after you post.`
              : 'Add up to five images.'
          }
        >
          <div className="community-listing-form__photos">
            <label className="community-listing-form__photo-add">
              <input
                ref={fileInputRef}
                id="listing-photos"
                type="file"
                accept="image/jpeg,image/png,image/webp"
                multiple
                onChange={onPhotoSelect}
                aria-label="Add photos to listing"
              />
              <FaPlus size={20} aria-hidden="true" />
              <span>Add</span>
            </label>
            {photoFiles.map((file, idx) => (
              <div key={`${file.name}-${idx}`} className="community-listing-form__photo-preview">
                <img src={URL.createObjectURL(file)} alt="" />
              </div>
            ))}
          </div>
          {photoFiles.length > 0 && onClearPhotos ? (
            <button type="button" className="y-btn y-btn-ghost y-btn-sm mt-2" onClick={onClearPhotos}>
              Clear photos
            </button>
          ) : null}
        </YFormField>
      ) : null}

      <YFormField
        label="Location"
        htmlFor="listing-location"
        labelClassName={LABEL_CLASS}
        help="City, region, or general area — buyers use this to find listings near them."
      >
        <PlaceSearchInput
          inputId="listing-location"
          value={form.location_text}
          onChange={onLocationTextChange}
          onSelect={onLocationSelect}
          placeholder="Search city, town, or address"
          limit={6}
          aria-label="Listing location"
        />
      </YFormField>
    </div>
  );
}
